import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import {
  getAllUsers,
  createUserByAdmin,
  resetUserPassword,
  toggleUserAdmin,
  deleteUser,
  updateUserByAdmin,
  getNotificationAdminSettings,
  updateNotificationAdminSettings,
  type User,
} from '../services/auth';
import { getErrorMessage } from '../lib/api';
import { Link, useNavigate } from 'react-router-dom';

const formatLastSeen = (date: string): string => {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return 'Lige nu';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min. siden`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} t. siden`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} dag${days !== 1 ? 'e' : ''} siden`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} md. siden`;
  return `${Math.floor(months / 12)} år siden`;
};

export default function AdminUsersPage() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createdPassword, setCreatedPassword] = useState('');
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [notificationsMutedForNonAdmins, setNotificationsMutedForNonAdmins] = useState(false);
  const [isSavingNotificationSetting, setIsSavingNotificationSetting] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  // Redirect if not admin
  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    loadUsers();
    loadNotificationSettings();
  }, []);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const data = await getAllUsers();
      setUsers(data);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const loadNotificationSettings = async () => {
    try {
      const settings = await getNotificationAdminSettings();
      setNotificationsMutedForNonAdmins(settings.mutedForNonAdmins);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleToggleNotificationMute = async () => {
    const nextValue = !notificationsMutedForNonAdmins;
    setIsSavingNotificationSetting(true);
    setError('');

    try {
      const updated = await updateNotificationAdminSettings(nextValue);
      setNotificationsMutedForNonAdmins(updated.mutedForNonAdmins);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSavingNotificationSetting(false);
    }
  };

  const handleCreateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsCreating(true);

    try {
      const result = await createUserByAdmin({
        email: newUserEmail,
        name: newUserName,
      });

      setCreatedPassword(result.temporaryPassword);
      setNewUserEmail('');
      setNewUserName('');
      await loadUsers();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsCreating(false);
    }
  };

  const handleResetPassword = async (userId: string) => {
    if (!confirm('Er du sikker på at du vil nulstille denne brugers adgangskode?')) {
      return;
    }

    try {
      const result = await resetUserPassword(userId);
      alert(`Ny midlertidig adgangskode: ${result.temporaryPassword}\n\nSend dette til brugeren via sikker kanal.`);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleToggleAdmin = async (u: User) => {
    const action = u.isAdmin ? 'fjerne adminrettigheder fra' : 'give adminrettigheder til';
    if (!confirm(`Er du sikker på at du vil ${action} ${u.name}?`)) return;
    try {
      const updated = await toggleUserAdmin(u.id, !u.isAdmin);
      setUsers((prev) => prev.map((x) => x.id === updated.id ? { ...x, isAdmin: updated.isAdmin } : x));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleDeleteUser = async (u: User) => {
    if (!confirm(`Er du sikker på at du vil slette brugeren "${u.name}"? Dette kan ikke fortrydes.`)) return;
    try {
      await deleteUser(u.id);
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  const handleStartEdit = (u: User) => {
    setEditingUserId(u.id);
    setEditName(u.name);
    setEditEmail(u.email);
    setError('');
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditName('');
    setEditEmail('');
  };

  const handleSaveEdit = async (userId: string) => {
    setIsSaving(true);
    setError('');
    try {
      const updated = await updateUserByAdmin(userId, { name: editName, email: editEmail });
      setUsers((prev) => prev.map((x) => x.id === updated.id ? { ...x, name: updated.name, email: updated.email } : x));
      setEditingUserId(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsSaving(false);
    }
  };

  if (!user?.isAdmin) {
    return null;
  }

  return (
    <div className="container mx-auto">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h1 className="font-heading font-bold" style={{ fontSize: 'clamp(1.4rem, 5vw, 2.5rem)' }}>Brugerstyring</h1>
        <div className="flex flex-wrap items-center gap-1.5">
          <Link to="/admin/audit-logs" className="btn" style={{ fontSize: '0.78rem', padding: '0.3em 0.8em' }}>
            Audit-log
          </Link>
          <Link to="/admin/release-notes" className="btn" style={{ fontSize: '0.78rem', padding: '0.3em 0.8em' }}>
            Release notes
          </Link>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="btn btn-primary"
            style={{ fontSize: '0.78rem', padding: '0.3em 0.8em' }}
          >
            {showCreateForm ? 'Annuller' : '+ Ny bruger'}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-400 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {showCreateForm && (
        <div className="card mb-3">
          <h2 className="text-h3 font-heading font-semibold mb-3">Opret ny bruger</h2>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                id="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                required
                className="input"
                placeholder="bruger@email.dk"
              />
            </div>

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                Navn
              </label>
              <input
                type="text"
                id="name"
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                required
                minLength={2}
                className="input"
                placeholder="Fulde navn"
              />
            </div>

            <button
              type="submit"
              disabled={isCreating}
              className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ fontSize: '0.85rem', padding: '0.4em 1em' }}
            >
              {isCreating ? 'Opretter...' : 'Opret bruger'}
            </button>
          </form>

          {createdPassword && (
            <div className="mt-4 p-4 bg-green-50 border border-green-400 rounded-lg">
              <p className="text-sm font-medium text-gray-900 mb-2">Bruger oprettet!</p>
              <p className="text-sm text-gray-700 mb-2">
                Midlertidig adgangskode: <code className="bg-white px-2 py-1 rounded font-mono">{createdPassword}</code>
              </p>
              <p className="text-xs text-gray-600">
                Send denne adgangskode til brugeren via sikker kanal. De skal ændre den ved første login.
              </p>
            </div>
          )}
        </div>
      )}

      <div className="card mb-3">
        <h2 className="text-h3 font-heading font-semibold mb-1">Testindstilling: Notifikationer</h2>
        <p className="text-sm text-gray-600 mb-3">
          Brug denne under test for at undgå unødige notifikationer til almindelige brugere.
          Admins modtager stadig notifikationer.
        </p>

        <button
          type="button"
          onClick={handleToggleNotificationMute}
          disabled={isSavingNotificationSetting}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
            notificationsMutedForNonAdmins
              ? 'bg-red-100 text-red-800 hover:bg-red-200'
              : 'bg-green-100 text-green-800 hover:bg-green-200'
          }`}
        >
          <span
            className={`w-2.5 h-2.5 rounded-full ${
              notificationsMutedForNonAdmins ? 'bg-red-600' : 'bg-green-600'
            }`}
          />
          {isSavingNotificationSetting
            ? 'Gemmer...'
            : notificationsMutedForNonAdmins
              ? 'Notifikationer er slået FRA for ikke-admins'
              : 'Notifikationer er slået TIL for alle'}
        </button>
      </div>

      <div className="card">
        <h2 className="text-h3 font-heading font-semibold mb-2">Alle brugere</h2>
        
        {isLoading ? (
          <p>Indlæser...</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Navn
                  </th>
                  <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Admin
                  </th>
                  <th className="hidden sm:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sidst online
                  </th>
                  <th className="px-2 sm:px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Handlinger
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((u) => (
                  <tr key={u.id}>
                    <td className="px-3 sm:px-4 py-2 sm:py-3 text-sm font-medium text-gray-900">
                      {editingUserId === u.id ? (
                        <>
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="input py-1 text-sm w-full"
                            placeholder="Fulde navn"
                            minLength={2}
                          />
                          <input
                            type="email"
                            value={editEmail}
                            onChange={(e) => setEditEmail(e.target.value)}
                            className="input py-1 text-sm w-full mt-1 sm:hidden"
                            placeholder="email@eksempel.dk"
                          />
                        </>
                      ) : (
                        <>
                          <span>{u.name}</span>
                          <span className="block sm:hidden text-xs text-gray-400 font-normal">{u.email}</span>
                          <span className="block sm:hidden text-xs text-gray-400 font-normal">
                            {u.isAdmin && <span className="text-purple-600 mr-2">Admin</span>}
                            {u.lastSeen ? formatLastSeen(u.lastSeen) : 'Aldrig online'}
                          </span>
                        </>
                      )}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                      {editingUserId === u.id ? (
                        <input
                          type="email"
                          value={editEmail}
                          onChange={(e) => setEditEmail(e.target.value)}
                          className="input py-1 text-sm w-full"
                          placeholder="email@eksempel.dk"
                        />
                      ) : (
                        u.email
                      )}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap">
                      {u.isAdmin ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                          Admin
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">–</span>
                      )}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {u.lastSeen ? formatLastSeen(u.lastSeen) : <span className="text-gray-300">Aldrig</span>}
                    </td>
                    <td className="px-2 sm:px-4 py-2 sm:py-3 text-sm font-medium">
                      {editingUserId === u.id ? (
                        <div className="flex flex-wrap gap-x-2 gap-y-1">  
                          <button
                            onClick={() => handleSaveEdit(u.id)}
                            disabled={isSaving || !editName.trim() || !editEmail.trim()}
                            className="text-green-600 hover:text-green-800 disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            {isSaving ? 'Gemmer...' : 'Gem'}
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            Annuller
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-x-2 gap-y-1">
                          <button
                            onClick={() => handleStartEdit(u)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Rediger
                          </button>
                          <button
                            onClick={() => handleToggleAdmin(u)}
                            disabled={u.id === user?.id}
                            className={u.isAdmin
                              ? 'text-orange-600 hover:text-orange-800 disabled:opacity-40 disabled:cursor-not-allowed'
                              : 'text-purple-600 hover:text-purple-800 disabled:opacity-40 disabled:cursor-not-allowed'
                            }
                            title={u.id === user?.id ? 'Du kan ikke ændre dine egne rettigheder' : undefined}
                          >
                            {u.isAdmin ? 'Fjern admin' : 'Giv admin'}
                          </button>
                          <button
                            onClick={() => handleResetPassword(u.id)}
                            className="text-primary-green hover:text-secondary-green"
                          >
                            Nulstil pw
                          </button>
                          <button
                            onClick={() => handleDeleteUser(u)}
                            disabled={u.id === user?.id}
                            className="text-red-600 hover:text-red-800 disabled:opacity-40 disabled:cursor-not-allowed"
                            title={u.id === user?.id ? 'Du kan ikke slette dig selv' : undefined}
                          >
                            Slet
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
